import { useState, useEffect, useTransition } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { CreateUserCommand, UpdateUserCommand, UpdateUserSecurityRolesCommand, UserDetail } from 'axiosApi/models';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { SCOPES } from "common/permissions";
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import { ENTITY_TYPE } from 'common/constants';
import ModalNewEmployee from 'components/User/ModalNewEmployee';
import Spinner from 'components/Spinner';
import { ButtonWithOptions } from "components/common/ButtonWithOptions";
import { handleAPIError } from 'common/errorHandler';
import ErrorSummary from 'components/common/ErrorSummary';
import { Box, Grid, Typography, TextField, Button, MenuItem, Stack, FormControlLabel, Switch } from '@mui/material';
import { AddCircle, ArrowBack, NoAccounts, Save } from '@mui/icons-material';
import UserEntitySelector from 'components/User/UserEntitySelector';
import Title from 'components/common/Title';
import { z, ZodType } from 'zod';

type UserForm = {
    id?: number;
    email: string;
    active: boolean;
    employees: UserDetail[];
    clients: UserDetail[];
    providers: UserDetail[];
    corporateEntities?: UserDetail[];
};

const userDetailZodSchema: ZodType<UserDetail> = z.object({
    id: z.number().optional(),
    entityType: z.nativeEnum(ENTITY_TYPE),
    entityId: z.number().int().min(1, intl.get('validate.errors.isRequired')),
    entityName: z.string().optional(),
    securityRoles: z.array(z.number()).optional(),
    isDefault: z.boolean().optional()
}).refine(data => {
    // If entityId is provided (greater than 0), securityRoles can't be empty
    return !(data.entityId > 0) || (data.securityRoles && data.securityRoles.length > 0);
}, {
    message: intl.get('validate.errors.securityRolesRequired'),
    path: ["securityRoles"]
});

const userFormSchema: ZodType<Partial<UserForm>> = z.object({
    id: z.number().optional(),
    email: z.string({ required_error: intl.get('validate.errors.isRequired') }).email(intl.get('validate.errors.isInvalid')),
    active: z.boolean().default(true),
    employees: z.array(userDetailZodSchema),
    clients: z.array(userDetailZodSchema),
    providers: z.array(userDetailZodSchema),
    corporateEntities: z.array(userDetailZodSchema).optional().refine(users => users?.length > 0, { message: intl.get('validate.errors.userWithoutEntity') })
}).refine(data => {
    const userDetails = [
        ...data.employees,
        ...data.clients,
        ...data.providers,
        ...(data.corporateEntities || [])
    ]
    const userWithoutEntity = userDetails.filter(user => user.entityId === 0);
    return userWithoutEntity.length === 0;
}, {
    message: intl.get('validate.errors.userWithoutEntity'),
}).refine(data => {
    const userDetails = [
        ...data.employees,
        ...data.clients,
        ...data.providers,
        ...(data.corporateEntities || [])
    ];

    const defaultUserEntity = userDetails.filter(user => user.isDefault && user.entityId !== 0);
    return defaultUserEntity.length <= 1;
}, {
    message: intl.get('validate.errors.defaultUserEntityRequired'),
});

const UserPage = () => {

    const DEFAULT_USER_ENTITY : UserDetail = {
        id: 0,
        entityType: ENTITY_TYPE.ANY,
        entityId: 0,
        entityName: '',
        securityRoles: [],
        isDefault: false
    };

    const DEFAULT_USER_INPUT : UserForm = {
        email: '',
        active: true,
        employees : [],
        clients: [],
        providers: [],
        corporateEntities: []
    };

    const api: Api = useApi();

    const toast = useToastMessageQueue();

    const [input, setInput] = useState(DEFAULT_USER_INPUT);

    const [disabled, setDisabled] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState({email: null});

    const [isUpdating, startUpdateTransition] = useTransition();

    // Helper function to determine if deletion should be disabled
    // A user must have at least one entity association total (across all types)
    const isDeleteDisabled = (entityType: ENTITY_TYPE, currentIndex: number): boolean => {
        const totalEntities = input.employees.length + input.clients.length + 
                             input.providers.length + (input.corporateEntities?.length || 0);
        
        // If there's only 1 total entity left, disable deletion
        return totalEntities <= 1;
    };


    const navigate = useNavigate();

    const { id } = useParams();
    const mode : 'EDIT' | 'NEW' = id ? 'EDIT' : 'NEW';

    const fetchUserDetail = async (id: number) => {
        setLoading(true);
        api.employeeApi.apiVversionUserIdGet(id, "1", {}).then((response) => {
            const { data } = response?.data;
            console.log("User detail data", data);
            const commonMap = (user: UserDetail) => ({
                id: user.id ?? 0,
                entityName: user.entityName || '',
                entityType: user.entityType,
                entityId: user.entityId,
                securityRoles: user.securityRoles || [],
                isDefault: user.isDefault || false,
            });
            setInput({
                email: data?.emailAddress || '',
                active: data?.active,
                employees: data?.userDetails
                    .filter(user => user.entityType === ENTITY_TYPE.EMPLOYEE)
                    .map(commonMap),
                clients: data?.userDetails
                    .filter(user => user.entityType === ENTITY_TYPE.CLIENT)
                    .map(commonMap),
                providers: data?.userDetails
                    .filter(user => user.entityType === ENTITY_TYPE.PROVIDER)
                    .map(commonMap),
                corporateEntities: data?.userDetails
                    .filter(user => user.entityType === ENTITY_TYPE.CORPORATE_ENTITY)
                    .map(commonMap),
            });

        }).catch((error) => {
            handleAPIError(error, toast, errors);    
            setErrors({ ...errors }); 
        }).finally(() => {
            setLoading(false);
        });
    }

    useEffect(() => {
        if (mode === 'EDIT' && id) {
            fetchUserDetail(parseInt(id));
        } else {
            setInput(DEFAULT_USER_INPUT);
        }
    }, []);

    const useFormToApiCommand = (input: UserForm): CreateUserCommand | UpdateUserCommand => {
        return {
            id: mode === 'EDIT' ? parseInt(id) : undefined,
            emailAddress: input.email,
            active: input.active,
            userDetails: [
                ...input.employees.map(user => ({
                    id: user.id,
                    entityType: user.entityType,
                    entityId: user.entityId,
                    securityRoles: user.securityRoles,
                    isDefault: user.isDefault || false,
                })).filter(user => user.entityId > 0), // Only include employees with entityId > 0
                ...input.clients.map(user => ({
                    id: user.id,
                    entityType: user.entityType,
                    entityId: user.entityId,
                    securityRoles: user.securityRoles,
                    isDefault: user.isDefault || false,
                })).filter(user => user.entityId > 0), // Only include clients with entityId > 0
                ...input.providers.map(user => ({
                    id: user.id,
                    entityType: user.entityType,
                    entityId: user.entityId,
                    securityRoles: user.securityRoles,
                    isDefault: user.isDefault || false,
                })).filter(user => user.entityId > 0), // Only include providers with entityId > 0
                ...(input.corporateEntities?.map(user => ({
                    id: user.id,
                    entityType: user.entityType,
                    entityId: user.entityId,
                    securityRoles: user.securityRoles,
                    isDefault: user.isDefault || false,
                })).filter(user => user.entityId > 0)), // Only include corporate entities with entityId > 0
            ]
        };
    }

    const handleEmailChange = (e) => {
        setInput({
            ...input,
            email: e?.target.value
        });
    };

    const addUser = (entityType: ENTITY_TYPE) => {
        switch (entityType) {
            case ENTITY_TYPE.EMPLOYEE:
                setInput({
                    ...input,
                    employees: input.employees.concat({
                        ...DEFAULT_USER_ENTITY,
                        entityType: entityType,
                    })
                });
                break;
            case ENTITY_TYPE.CLIENT:
                setInput({
                    ...input,
                    clients: input.clients.concat({
                        ...DEFAULT_USER_ENTITY,
                        entityType: entityType,
                    })
                });
                break;
            case ENTITY_TYPE.PROVIDER:
                setInput({
                    ...input,
                    providers: input.providers.concat({
                        ...DEFAULT_USER_ENTITY,
                        entityType: entityType,
                    })
                });
                break;
            case ENTITY_TYPE.CORPORATE_ENTITY:
                setInput({
                    ...input,
                    corporateEntities: input.corporateEntities?.concat({
                        ...DEFAULT_USER_ENTITY,
                        entityType: entityType,
                    })
                });
                break;
            default:
                throw new Error(`Unknown entity type: ${entityType}`);
        }
    };

    const validate = function (submitInput) {
        setDisabled(false);
        let errors: any = {email: null, userWithoutEntity: null};
        if (!submitInput.email) {
            errors.email = intl.get('validate.errors.isRequired');
            setDisabled(true);
        } else if (!/\S+@\S+\.\S+/.test(submitInput.email)) {
            errors.email = intl.get('validate.errors.isInvalid');
            setDisabled(true);
        };
        submitInput

        const userWithoutEntity = submitInput.userDetails.filter(user => user.entityId === 0 && user.securityRoles.length > 0);
        if (userWithoutEntity.length > 0) {
            errors.userWithoutEntity = intl.get('validate.errors.isRequired');
            setDisabled(true);
        };
        return errors;
    };

    const validateForm: (input: UserForm) => z.SafeParseReturnType<Partial<UserForm>, Partial<UserForm>> = (input: UserForm) => {
        const submitInput = {
            email: input.email,
            active: input.active,
            userDetails: [
                ...input.employees.filter(user => user.entityId),
                ...input.clients.filter(user => user.entityId),
                ...input.providers.filter(user => user.entityId),
                ...(input.corporateEntities?.filter(user => user.entityId) || [])
            ]
        };
        return userFormSchema.safeParse(submitInput);
    }
    
    const handleSubmit = async (close: boolean) => {
        const submitInput = {
            email: input.email,
            active: input.active,
            userDetails: [
                ...input.employees.filter(user => user.entityId),
                ...input.clients.filter(user => user.entityId),
                ...input.providers.filter(user => user.entityId),
                ...(input.corporateEntities?.filter(user => user.entityId) || [])
            ]
        };
        const validationResult = validateForm(input);

        const errors = validate(submitInput);
        setErrors(errors);
        if (errors.email === null) {
            let cmd: CreateUserCommand | UpdateUserCommand = {
                id: mode === 'EDIT' ? parseInt(id) : undefined,
                emailAddress: submitInput.email,
                active: submitInput.active,
                userDetails: submitInput.userDetails?.map(userDetail => ({
                    id: userDetail.id,
                    entityType: userDetail.entityType,
                    entityId: userDetail.entityId,
                    securityRoles: userDetail.securityRoles,
                    isDefault: userDetail.isDefault,
                }))
            };

            const promise = mode === 'EDIT'
                ? api.employeeApi.apiVversionUserPut("1", cmd, {})
                : api.employeeApi.apiVversionUserPost("1", cmd, {});

            startUpdateTransition(async () => {
                await promise.then((response) => {
                    const { data } = response?.data;
                    toast.success({ message: intl.get(mode === 'NEW'?'newUser.toast.success.handleSave.add':'newUser.toast.success.handleSave') });
                    if (close) {
                        navigate(`/users`);
                    }
                    if (mode === 'NEW') {
                        navigate(`/user/detail/${data.id}`);
                    }
                }).catch((error) => {
                    handleAPIError(error, toast, errors);
                    setErrors({ ...errors });
                    setLoading(false);
                })
            });
        };
    };

    const handleBackClick = (e) => {
        navigate('/users');
    };

    const handleSaveCloseClick = () => {
        handleSubmit(true);
    };

    const handleSaveClick = () => {
        handleSubmit(false);
    };

    const onChangeUserEntity = (userEntity: UserDetail, index: number, entityType: ENTITY_TYPE) => {
        let userDetails = {};
        switch (entityType) {
            case ENTITY_TYPE.EMPLOYEE:
                userDetails = {
                    employees: input.employees.map((user, uindex) => uindex === index ? userEntity : user)
                };
                break;
            case ENTITY_TYPE.CLIENT:
                userDetails = {
                    clients: input.clients.map((user, uindex) => uindex === index ? userEntity : user)
                };
                break;
            case ENTITY_TYPE.PROVIDER:
                userDetails = {
                    providers: input.providers.map((user, uindex) => uindex === index ? userEntity : user)
                };
                break;
            case ENTITY_TYPE.CORPORATE_ENTITY:
                userDetails = {
                    corporateEntities: input.corporateEntities?.map((user, uindex) => uindex === index ? userEntity : user)
                };
                break;
            default:
                throw new Error(`Unknown entity type: ${entityType}`);
        }

        setInput((prevInput) => ({
            ...prevInput,
            ...userDetails,
        }));
    }

    const handleDefaultChange = (changedUserEntity: UserDetail, isDefault: boolean) => {
        // Find which array and index the changed entity belongs to
        let entityType: ENTITY_TYPE;
        let entityIndex = -1;

        // Find the entity in employees
        entityIndex = input.employees.findIndex(user => 
            user.entityType === changedUserEntity.entityType && 
            user.entityId === changedUserEntity.entityId
        );
        if (entityIndex !== -1) {
            entityType = ENTITY_TYPE.EMPLOYEE;
        } else {
            // Find the entity in clients
            entityIndex = input.clients.findIndex(user => 
                user.entityType === changedUserEntity.entityType && 
                user.entityId === changedUserEntity.entityId
            );
            if (entityIndex !== -1) {
                entityType = ENTITY_TYPE.CLIENT;
            } else {
                // Find the entity in providers
                entityIndex = input.providers.findIndex(user => 
                    user.entityType === changedUserEntity.entityType && 
                    user.entityId === changedUserEntity.entityId
                );
                if (entityIndex !== -1) {
                    entityType = ENTITY_TYPE.PROVIDER;
                } else {
                    // Find the entity in corporateEntities
                    entityIndex = input.corporateEntities?.findIndex(user => 
                        user.entityType === changedUserEntity.entityType && 
                        user.entityId === changedUserEntity.entityId
                    ) || -1;
                    if (entityIndex !== -1) {
                        entityType = ENTITY_TYPE.CORPORATE_ENTITY;
                    }
                }
            }
        }

        if (entityIndex !== -1 && entityType) {
            // Update the specific entity and call onChangeUserEntity to trigger API call
            const updatedUserEntity = { ...changedUserEntity, isDefault: isDefault };
            onChangeUserEntity(updatedUserEntity, entityIndex, entityType);

            // If setting this entity as default, unset all other defaults
            if (isDefault) {
                const updatedInput = {
                    ...input,
                    employees: input.employees.map(user => ({ 
                        ...user, 
                        isDefault: user.entityType === changedUserEntity.entityType && 
                                   user.entityId === changedUserEntity.entityId ? true : false 
                    })),
                    clients: input.clients.map(user => ({ 
                        ...user, 
                        isDefault: user.entityType === changedUserEntity.entityType && 
                                   user.entityId === changedUserEntity.entityId ? true : false 
                    })),
                    providers: input.providers.map(user => ({ 
                        ...user, 
                        isDefault: user.entityType === changedUserEntity.entityType && 
                                   user.entityId === changedUserEntity.entityId ? true : false 
                    })),
                    corporateEntities: input.corporateEntities?.map(user => ({ 
                        ...user, 
                        isDefault: user.entityType === changedUserEntity.entityType && 
                                   user.entityId === changedUserEntity.entityId ? true : false 
                    })) || []
                };
                setInput(updatedInput);
            }
        }
    };    const deleteUserEntity = async (deletedIndex, entityType) => {
        const userDetails = {
            employees: input.employees,
            clients: input.clients,
            providers: input.providers,
            corporateEntities: input.corporateEntities
        }
        entityType === ENTITY_TYPE.EMPLOYEE && {
            employees: input.employees.filter((_, index) => index !== deletedIndex)
        } ||
        entityType === ENTITY_TYPE.CLIENT && {
            clients: input.clients.filter((_, index) => index !== deletedIndex)
        } ||
        entityType === ENTITY_TYPE.PROVIDER && {
            providers: input.providers.filter((_, index) => index !== deletedIndex)
        } ||
        entityType === ENTITY_TYPE.CORPORATE_ENTITY && {
            corporateEntities: input.corporateEntities?.filter((_, index) => index !== deletedIndex)
        };
        let targetEntity;
        switch (entityType) {
            case ENTITY_TYPE.EMPLOYEE:
                targetEntity = userDetails.employees[deletedIndex];
                userDetails.employees = userDetails.employees.filter((_, index) => index !== deletedIndex);
                break;
            case ENTITY_TYPE.CLIENT:
                targetEntity = input.clients[deletedIndex];
                userDetails.clients = userDetails.clients.filter((_, index) => index !== deletedIndex);
                break;
            case ENTITY_TYPE.PROVIDER:
                targetEntity = input.providers[deletedIndex];
                userDetails.providers = userDetails.providers.filter((_, index) => index !== deletedIndex);
                break;
            case ENTITY_TYPE.CORPORATE_ENTITY:
                targetEntity = input.corporateEntities?.[deletedIndex];
                userDetails.corporateEntities = userDetails.corporateEntities?.filter((_, index) => index !== deletedIndex);
                break;
            default:
                throw new Error(`Unknown entity type: ${entityType}`);
        }
        setInput((prevInput) => ({
            ...prevInput,
            ...userDetails,
        }));
    }

    const onChangeStatus = (active: boolean) => {
        setInput((prevInput) => ({
            ...prevInput,
            active,
        }));
        //save
        handleSubmit(false);
    }

    const title = mode === 'EDIT' ? intl.get('userDetail.header') : intl.get('newUser.header');

    return (
        <Box sx={{ p: 3 }}>
            <Title>{title}</Title>
            <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
                {title}
            </Typography>

            <ErrorSummary errors={errors} showEverything={false} mode="warning" />
            <PermissionsGate
                viewScopes={[SCOPES['users.read']]}
                editScopes={[SCOPES['users.edit']]}
                viewRoles={null}
                editRoles={null}
                RenderError={() => <span>{intl.get('permissionsGate')}</span>}
            >
                {loading ? (
                    <Spinner />
                ) : (
                    <Box component="form">
                        <Grid container spacing={2}>
                            <Grid size={{xs:9}}>
                                <TextField
                                    id="userEmail"
                                    name="email"
                                    fullWidth
                                    placeholder={intl.get('newUser.placeholder.email')}
                                    value={input?.email}
                                    onChange={handleEmailChange}
                                    error={errors.email !== "" && errors.email !== null}
                                    helperText={errors.email}
                                    size="small"
                                />
                            </Grid>
                            {mode === 'EDIT' && (
                                <Grid size={{xs:3}} display="flex" alignItems="center">
                                    <FormControlLabel
                                        disabled={isUpdating}
                                        checked={input.active}
                                        control={<Switch />}
                                        onChange={(e, checked) => onChangeStatus(checked)}
                                        label={input.active ? intl.get('roleRow.active') : intl.get('roleRow.inactive')}
                                        labelPlacement="end"
                                    />
                                </Grid>
                            )}
                        </Grid>   
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {intl.get('newUser.assignment.title')}
                            </Typography>
                            <Typography>
                                {intl.get('newUser.assignment.message')}
                            </Typography>
                        </Box>
                        
                        <Stack gap={1}>
                            <Typography component="label" htmlFor="userAsEmployee">
                                {intl.get('newUser.userAsEmployee')}
                            </Typography>
                            <Stack gap={2}>
                                {input.employees?.length === 0 && (
                                    <Typography display={"flex"} alignItems={"center"} gap={1} variant="caption" color="text.secondary">
                                        <NoAccounts />{intl.get('users.userAsEmployee.empty')}
                                    </Typography>
                                )}
                                {input.employees.map((comp, index) => (
                                    <UserEntitySelector
                                        key={`employee-${index}`}
                                        entityTypeAheadProps={{
                                            readonly: mode === 'EDIT' && comp.id > 0,
                                            disabled: isUpdating
                                        }}
                                        userEntity={comp}
                                        setUserEntity={(userEntity) => {
                                            onChangeUserEntity(userEntity, index, ENTITY_TYPE.EMPLOYEE);
                                        }}
                                        onDelete={() => deleteUserEntity(index, ENTITY_TYPE.EMPLOYEE) }
                                        disabledDelete={isDeleteDisabled(ENTITY_TYPE.EMPLOYEE, index)}
                                        onDefaultChange={handleDefaultChange}
                                    />
                                ))}
                            </Stack>
                                
                            <Grid container spacing={2} padding={1}>
                                <Grid size={{xs: 12, md: 6 }}>
                                    <ModalNewEmployee />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }} display={"flex"} justifyContent={'end'}>
                                    <Button
                                        variant='text'
                                        onClick={() => addUser(ENTITY_TYPE.EMPLOYEE)}
                                        startIcon={<AddCircle />}
                                    >
                                        {intl.get('newUser.addEmployee')}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Stack>
                        
                        <Box component="hr" className="separator" sx={{ my: 2 }} />
                        
                        <Stack gap={1}>
                            <Typography component="label" htmlFor="userAsClient">
                                {intl.get('newUser.userAsClient')}
                            </Typography>
                            <Stack gap={2}>
                                {input.clients?.length === 0 && (
                                    <Typography display={"flex"} alignItems={"center"} gap={1} variant="caption" color="text.secondary">
                                        <NoAccounts />{intl.get('users.userAsClient.empty')}
                                    </Typography>
                                )}
                                {input.clients.map((comp, index) => (
                                    <UserEntitySelector
                                        key={`client-${index}`}
                                        entityTypeAheadProps={{
                                            readonly: mode === 'EDIT' && comp.id > 0,
                                            disabled: isUpdating
                                        }}
                                        userEntity={comp}
                                        setUserEntity={(userEntity) => {
                                            onChangeUserEntity(userEntity, index, ENTITY_TYPE.CLIENT);
                                        }}
                                        onDelete={() => deleteUserEntity(index, ENTITY_TYPE.CLIENT) }
                                        disabledDelete={isDeleteDisabled(ENTITY_TYPE.CLIENT, index)}
                                        onDefaultChange={handleDefaultChange}
                                    />
                                ))}
                            </Stack>
                                
                            <Grid container padding={1}>
                                <Grid size={12} display={"flex"} justifyContent={'end'}>
                                    <Button
                                        variant='text'
                                        onClick={() => addUser(ENTITY_TYPE.CLIENT)}
                                        startIcon={<AddCircle />}
                                    >
                                        {intl.get('newUser.addClient')}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Stack>
                        
                        <Box component="hr" className="separator" sx={{ my: 2 }} />
                        
                        <Stack gap={1}>
                            <Typography component="label" htmlFor="userAsProvider">
                                {intl.get('newUser.userAsProvider')}
                            </Typography>
                            <Stack gap={2}>
                                {input.providers?.length === 0 && (
                                    <Typography display={"flex"} alignItems={"center"} gap={1} variant="caption" color="text.secondary">
                                        <NoAccounts />{intl.get('users.userAsProvider.empty')}
                                    </Typography>
                                )}
                                {input.providers.map((comp, index) => (
                                    <UserEntitySelector
                                        key={`provider-${index}`}
                                        entityTypeAheadProps={{
                                            readonly: mode === 'EDIT' && comp.id > 0,
                                            disabled: isUpdating
                                        }}
                                        userEntity={comp}
                                        setUserEntity={(userEntity) => {
                                            onChangeUserEntity(userEntity, index, ENTITY_TYPE.PROVIDER);
                                        }}
                                        onDelete={() => deleteUserEntity(index, ENTITY_TYPE.PROVIDER) }
                                        disabledDelete={isDeleteDisabled(ENTITY_TYPE.PROVIDER, index)}
                                        onDefaultChange={handleDefaultChange}
                                    />
                                ))}
                            </Stack>
                                
                            <Grid container padding={1}>
                                <Grid size={12} display={"flex"} justifyContent={'end'}>
                                    <Button
                                        variant='text'
                                        onClick={() => addUser(ENTITY_TYPE.PROVIDER)}
                                        startIcon={<AddCircle />}
                                    >
                                        {intl.get('newUser.addProvider')}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Stack>

                        <Box component="hr" className="separator" sx={{ my: 2 }} />
                        
                        <Stack gap={1}>
                            <Typography component="label" htmlFor="userAsCorporateEntity">
                                {intl.get('newUser.userAsCorporateEntity')}
                            </Typography>
                            <Stack gap={2}>
                                {input.corporateEntities?.length === 0 && (
                                    <Typography display={"flex"} alignItems={"center"} gap={1} variant="caption" color="text.secondary">
                                        <NoAccounts />{intl.get('users.userAsCorporateEntity.empty')}
                                    </Typography>
                                )}
                                {input.corporateEntities.map((comp, index) => (
                                    <UserEntitySelector
                                        key={`corporate-entity-${index}`}
                                        entityTypeAheadProps={{
                                            readonly: mode === 'EDIT' && comp.id > 0,
                                            disabled: isUpdating
                                        }}
                                        userEntity={comp}
                                        setUserEntity={(userEntity) => {
                                            onChangeUserEntity(userEntity, index, ENTITY_TYPE.CORPORATE_ENTITY);
                                        }}
                                        onDelete={() => deleteUserEntity(index, ENTITY_TYPE.CORPORATE_ENTITY) }
                                        disabledDelete={isDeleteDisabled(ENTITY_TYPE.CORPORATE_ENTITY, index)}
                                        onDefaultChange={handleDefaultChange}
                                    />
                                ))}
                            </Stack>
                                
                            <Grid container padding={1}>
                                <Grid size={12} display={"flex"} justifyContent={'end'}>
                                    <Button
                                        variant='text'
                                        onClick={() => addUser(ENTITY_TYPE.CORPORATE_ENTITY)}
                                        startIcon={<AddCircle />}
                                    >
                                        {intl.get('newUser.addCorporateEntity')}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Stack>
                        
                        <Grid container spacing={2} sx={{ mt: 3 }}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Typography variant="caption" color="text.secondary">
                                    * Required fields
                                </Typography>
                            </Grid>
                            <Grid
                                sx={{ display: 'flex', justifyContent: 'flex-end', my: 3 }}
                                size={{ xs: 12, md: 8 }}
                            >
                                <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} gap={1}>
                                    <Button onClick={handleBackClick}>
                                        <ArrowBack />
                                    </Button>
                                    <PermissionsGate
                                        viewScopes={[SCOPES['users.read']]}
                                        editScopes={[SCOPES['users.edit']]}
                                        viewRoles={[]}
                                        editRoles={[]}
                                        RenderError={() => <span>{intl.get('permissionsGate')}</span>}
                                    >
                                        <ButtonWithOptions
                                            onClick={handleSaveClick}
                                            startIcon={<Save />}
                                            disabled={disabled || isUpdating}
                                            menu={[
                                                <MenuItem key={"save-and-close"} onClick={handleSaveCloseClick}>{intl.get('save&close.button')}</MenuItem>
                                            ]}
                                        >
                                            {intl.get('save.button')}
                                        </ButtonWithOptions>
                                    </PermissionsGate>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </PermissionsGate>
        </Box>
    );
};

export default UserPage;
